import {device} from "@atg/utils";
import {noTrans} from "@atg-shared/no-trans";
import {isSportApp} from "@atg-shared/system";
// eslint-disable-next-line @nx/enforce-module-boundaries
import {createTheme} from "@atg-ui/toolkit/theme";

export type KambiCustomerSettings = {
    enablePrematchStats?: boolean;
    fixedHeaderHeight: number;
    hideHeader?: boolean;
    enableFilterMenu?: boolean;
    enableTermSearch?: boolean;
    reservedRoutes?: string[];
    emptyClientRoutes?: string[];
    customizedBetslipFeedbackTypes?: string[];
    prioritiseClientApiLoading?: boolean;
    harmonizedEventPage?: boolean;
    useSandwichFilterForBetOfferCategoryFilter?: boolean;
    eventpageDoubleSandwichFilterEnabled?: boolean;
    enableBrowseSports?: boolean;
    enableSportsHub?: boolean;
    enableHarmonizedScoreboard?: boolean;
    enableHomeOrAwayLabelForHarmonizedScoreboard?: boolean;
    enableHamonizedSettingsPage?: boolean;
    enableMyBetsHarmonization?: boolean;
    enableSandwichFilterLiveRightNow?: boolean;
    enableSandwichFilterStartingSoon?: boolean;
    enableGlobalHeader?: boolean;
    enableNavigationPanel?: boolean;
    enableClientHarmonization?: boolean;
    searchBarVisibility?: {
        [key: string]: boolean;
    };
    useShorthandForOverUnder?: boolean;
    enableNewSearch?: boolean;
    enablePopularSports?: boolean;
    headerHiddenRoutes?: string[];
    mobileKeyboardDefaultStakes?: {
        behaviour: "add" | "replace";
        showUnsorted: boolean;
        defaultStakes: Array<{
            label: string;
            value: number;
        }>;
    };
};

export type KambiUserSettings = {
    oddsFormat: string;
    currency: string;
    locale: string;
    market: string;
    playerId?: string;
    ticket?: string;
    streamingAllowedForPlayer: boolean;
    allowCookies?: boolean;
    environment: string;
    offering: string;
};

/*
 * GA has issues reading query params that are between the base path and kambi's
 * hash-routing, so they have to be placed after the hashroute, making them part of
 * kambi's route. In order for our reserved/empty routes to work even when they
 * contain query params (utm-tags mainly), we optionally match a question mark which
 * can be followed by any characters as long as they occur at the end of a route.
 */
export const withOptionalParams = (route: string): string => `${route}(?:\\?.*)?`;

/**
 * Number of hours considered in starting soon. Since the new layout has a range of
 * intervals, we set it to the maximum range of 24 hours.
 */
export const startingSoonInterval = 24;

const theme = createTheme();
const getBreakpoint = theme.breakpoints.down("md").replace("@media ", "");
const isMobile =
    (typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia(getBreakpoint).matches) ||
    device.isTouchDevice();

const getFixedHeaderHeight = () => {
    if (isSportApp) {
        return 0;
    }

    if (isMobile) {
        return 158; // Height of spelpaus + new header + new bottom bar
    }

    return 34; // Height of spelpaus,
};

const sportAppRoutesEmpty = isSportApp ? ["statistics", "big9/*", "daily5/*"] : [];

export const DEFAULT_CUSTOMER_SETTINGS: KambiCustomerSettings = {
    /**
     * Toggles visibility of the prematch stats widgets (gm-team-performance and gm-head-to-head widgets) on the event page
     * @type Object or Boolean
     * NOTE: Overriding this setting overrides complete value so it would not merge object, it would replace it.
     */
    enablePrematchStats: true,

    /**
     * Allocate space for header element at the top of the client
     * This will reduce the height of the betting client with the number passed in (pixels) so the betting client will have the height of the viewport
     * minus the passed in fixeHeaderHeight value
     * @type Number
     * @default 0
     */
    fixedHeaderHeight: getFixedHeaderHeight(),

    /**
     * Hide header component in the client
     * @type Boolean
     * @default false
     */
    hideHeader: true,

    /**
     * Show filter component
     * @type Boolean
     * @default true
     */
    enableFilterMenu: false,

    /**
     * Show search component
     * @type Boolean
     * @default true
     */
    enableTermSearch: false,

    /**
     * Renders empty page in the client for specified routes
     * Example: ['^home', 'filter/all/all/all/all/in-play']
     * will render empty page instead of start page and the live right now page (note the omitting of the hash #).
     * Note that the client will not load resources and will not render the original page which is better for browser performance.
     * Therefore this setting should be preferred over "emptyClientRoutes" if the original client page is not to be used at all.
     * RegExp string patterns can be used.
     * @type Array of strings
     * @default []
     * NOTE: Overriding this setting overrides complete value so it would not merge array, it would replace it.
     */
    reservedRoutes: ["^home", "popular-events", ...sportAppRoutesEmpty]
        .filter(Boolean)
        .map(withOptionalParams),

    /**
     * Hides the entire client (including header, footer and navigation) on specified routes.
     * Example: ['home', 'filter/all/all/all/all/in-play']
     * will hide the client on start page and the live right now page (note the omitting of the hash #).
     * Note that the client will continue loading resources and updating the hidden page which can affect the browser performance.
     * The client can be made visible using WAPI (CLIENT_SHOW method).
     * RegExp string patterns can be used but without:
     * start char ^ and end char $
     * @type Array of strings
     * @default []
     * NOTE: Overriding this setting overrides complete value so it would not merge array, it would replace it.
     */
    emptyClientRoutes: ["home(/.*)?", "popular-events", ...sportAppRoutesEmpty]
        .filter(Boolean)
        .map(withOptionalParams),

    /**
     * States that we want to get notified when the client gets this error trying to place bets and not being logged in
     * (see documentation about notifications for more info)
     * NOTE: Overriding this setting overrides complete value so it would not merge array, it would replace it.
     */
    customizedBetslipFeedbackTypes: ["VET_COUPON_UNFUNDED", "VET_NOT_ENOUGH_FUNDS"],

    /**
     * Sets higher priority for ClientApi component loading during the start-up of the client.
     * It makes WAPI ready to communicate with the client earlier.
     * Note that enabling this setting increases the time of initial loading and rendering of the client.
     * @type Boolean
     * @default false
     */
    prioritiseClientApiLoading: true,

    /**
     * Renders the harmonised version of the event page
     * @type Boolean
     */
    harmonizedEventPage: false,

    /**
     * Use the sandwich filter for the betoffer category filter on the event page
     * @type Boolean
     */
    useSandwichFilterForBetOfferCategoryFilter: true,

    /**
     * Enable double sw filter on event page
     * @type Boolean
     */
    eventpageDoubleSandwichFilterEnabled: true,

    /**
     * Enables browse sports page
     * @type Boolean
     */
    enableBrowseSports: true,

    /**
     * Enables sportshub page
     * @type Boolean
     */
    enableSportsHub: true,

    /**
     * Enables harmonised scoreboard
     * @type Boolean
     */
    enableHarmonizedScoreboard: false,

    /**
     * Adds Home or Away before the team name in the harmonized scoreboard
     * @type Boolean
     */
    enableHomeOrAwayLabelForHarmonizedScoreboard: true,

    /**
     * Enables new harmonised settings page version
     * @type Boolean
     */
    enableHamonizedSettingsPage: true,

    /**
     * Enables new harmonised my bets page
     * @type Boolean
     */
    enableMyBetsHarmonization: true,

    /**
     * Enables sw filter on LRN page
     * @type Boolean
     */
    enableSandwichFilterLiveRightNow: true,

    /**
     * Enables sw filter on Starting soon page
     * @type Boolean
     */
    enableSandwichFilterStartingSoon: true,

    /**
     * Enables the global header, and in the process removes the separate search triggers and breadcrumbs on each page
     * @type Boolean
     */
    enableGlobalHeader: true,

    /**
     * Enables left-side navigation.
     * @type Boolean
     * @default true
     */
    enableNavigationPanel: false,

    /**
     * Enables new A_Z page
     * @type Boolean
     */
    enableClientHarmonization: true,

    /**
     * Search bar visibility on different pages
     * @type Object
     */
    searchBarVisibility: {
        landingPage: false,
        azPage: true,
        sportsHub: false,
    },

    /**
     * Show "Ö" and "U" instead of "Över" and "Under"
     * @type Boolean
     */
    useShorthandForOverUnder: true,

    /**
     * Enables the new search results
     * @type Boolean
     */
    enableNewSearch: true,

    /**
     * Show popular sports instead of browsing leagues
     * @type Boolean
     */
    enablePopularSports: true,

    /**
     * Routes where the header should be hidden
     * @type Array of strings
     * @default []
     */
    headerHiddenRoutes: [
        "FILTER",
        "STARTING_SOON",
        "IN_PLAY",
        "EVENT",
        "BET_HISTORY",
        "REWARDS",
        "SETTINGS",
        "SPORTS_HUB",
        "PARTICIPANT",
    ],

    /**
     * Preset stake buttons shown in the mobile keyboard.
     * Clicking a button applies its value to the current stake input.
     * With behaviour set to "add", the selected value is added to the current stake.
     * @type Object
     */
    mobileKeyboardDefaultStakes: {
        behaviour: "add",
        showUnsorted: false,
        defaultStakes: [
            {label: "+10", value: 10},
            {label: "+50", value: 50},
            {label: "+100", value: 100},
            {label: "+200", value: 200},
            {label: "+500", value: 500},
        ],
    },
};

export enum CustomElements {
    EventStatisticsButton = "event-statistics-button",
    EsportPregame = "esport-pregame",
    EsportStream = "esport-stream",
    EsportScoreboard = "esport-scoreboard",
    ShortMatchInfo = "short-match-info",
    InsufficientBalance = "insufficient-balance",
    BetslipBalance = "betslip-balance",
    PrematchStatistics = "prematch-statistics",
    EventLeagueTable = "event-league-table",
    HeadToHead = "head-to-head",
    LoginBanner = "login-banner",
    BetBuilderVideo = "bet-builder-video",
    MatchPreview = "match-preview",
    MatchCommentsCarousel = "match-comments-carousel",
    NewsCarousel = "news-carousel",
    VideosCarousel = "videos-carousel",
    OddsboostCarousel = "oddsboost-carousel",
    PopularMatchesCarousel = "popular-matches-carousel",
    StatsHub = "stats-hub",
    PopularParticipantsCarousel = "popular-participants-carousel",
}

export const WIDGET_IDS = {
    betslipBalance: noTrans("BetslipBalance"),
    insufficientBalance: noTrans("InsufficientBalance"),
    eventLeagueTable: noTrans("EventLeagueTable"),
    headToHead: noTrans("HeadToHead"),
    loginBanner: noTrans("LoginBanner"),
    eventStatisticsButton: noTrans("EventStatisticsButton"),
    esportPregame: noTrans("EsportPregame"),
    esportStream: noTrans("EsportStream"),
    esportScoreboard: noTrans("EsportScoreboard"),
    shortMatchInfo: noTrans("ShortMatchInfo"),
    prematchStatistics: noTrans("PrematchStatistics"),
    betBuilderVideo: noTrans("BetBuilderVideo"),
    matchPreview: noTrans("MatchPreview"),
    matchCommentsCarousel: noTrans("MatchCommentsCarousel"),
    newsCarousel: noTrans("NewsCarousel"),
    videosCarousel: noTrans("VideosCarousel"),
    oddsboostCarousel: noTrans("OddsboostCarousel"),
    popularMatchesCarousel: noTrans("PopularMatchesCarousel"),
    statsHub: noTrans("StatsHub"),
    popularParticipantsCarousel: noTrans("PopularParticipantsCarousel"),
};

type DeepPartial<T> = T extends Array<infer Item>
    ? Array<DeepPartial<Item>>
    : T extends object
    ? {
          [Key in keyof T]?: DeepPartial<T[Key]>;
      }
    : T;

export type KambiWidgetSettings = DeepPartial<typeof WIDGET_SETTINGS>;

export const WIDGET_SETTINGS = {
    // https://docs.kambipartner.com/tech-hub/current/default-kambi-components-defined-as-widgets
    hidden: ["gm-league-table", "gm-team-performance", "gm-head-to-head"],
    // https://docs.kambipartner.com/tech-hub/current/default-kambi-components-defined-as-widgets
    resets: ["gm-league-table", "gm-team-performance", "gm-head-to-head"],
    // https://docs.kambipartner.com/tech-hub/current/widgets
    widgets: [
        {
            widgetId: WIDGET_IDS.betslipBalance,
            widgetType: "web-component",
            tag: CustomElements.BetslipBalance,
            height: 38,
        },
        {
            widgetId: WIDGET_IDS.insufficientBalance,
            widgetType: "web-component",
            tag: CustomElements.InsufficientBalance,
            height: 58,
            conditions: {
                authenticated: true,
            },
        },
        {
            widgetId: WIDGET_IDS.eventLeagueTable,
            widgetType: "web-component",
            tag: CustomElements.EventLeagueTable,
        },
        {
            widgetId: WIDGET_IDS.headToHead,
            widgetType: "web-component",
            tag: CustomElements.HeadToHead,
        },
        {
            widgetId: WIDGET_IDS.loginBanner,
            widgetType: "web-component",
            tag: CustomElements.LoginBanner,
        },
        {
            widgetId: WIDGET_IDS.eventStatisticsButton,
            widgetType: "web-component",
            tag: CustomElements.EventStatisticsButton,
        },
        {
            widgetId: WIDGET_IDS.esportPregame,
            widgetType: "web-component",
            tag: CustomElements.EsportPregame,
        },
        {
            widgetId: WIDGET_IDS.esportStream,
            widgetType: "web-component",
            tag: CustomElements.EsportStream,
        },
        {
            widgetId: WIDGET_IDS.esportScoreboard,
            widgetType: "web-component",
            tag: CustomElements.EsportScoreboard,
        },
        {
            widgetId: WIDGET_IDS.shortMatchInfo,
            widgetType: "web-component",
            tag: CustomElements.ShortMatchInfo,
        },
        {
            widgetId: WIDGET_IDS.prematchStatistics,
            widgetType: "web-component",
            tag: CustomElements.PrematchStatistics,
        },
        {
            widgetId: WIDGET_IDS.betBuilderVideo,
            widgetType: "web-component",
            tag: CustomElements.BetBuilderVideo,
        },
        {
            widgetId: WIDGET_IDS.matchPreview,
            widgetType: "web-component",
            tag: CustomElements.MatchPreview,
        },
        {
            widgetId: WIDGET_IDS.matchCommentsCarousel,
            widgetType: "web-component",
            tag: CustomElements.MatchCommentsCarousel,
        },
        {
            widgetId: WIDGET_IDS.newsCarousel,
            widgetType: "web-component",
            tag: CustomElements.NewsCarousel,
        },
        {
            widgetId: WIDGET_IDS.videosCarousel,
            widgetType: "web-component",
            tag: CustomElements.VideosCarousel,
        },
        {
            widgetId: WIDGET_IDS.oddsboostCarousel,
            widgetType: "web-component",
            tag: CustomElements.OddsboostCarousel,
        },
        {
            widgetId: WIDGET_IDS.popularMatchesCarousel,
            widgetType: "web-component",
            tag: CustomElements.PopularMatchesCarousel,
        },
        {
            widgetId: WIDGET_IDS.statsHub,
            widgetType: "web-component",
            tag: CustomElements.StatsHub,
        },
        {
            widgetId: WIDGET_IDS.popularParticipantsCarousel,
            widgetType: "web-component",
            tag: CustomElements.PopularParticipantsCarousel,
        },
    ],
    // https://docs.kambipartner.com/tech-hub/current/widget-target-areas
    targets: {
        // Places the widgets at the very top of an event page
        eventTop: [
            {
                order: 1,
                widgetId: WIDGET_IDS.loginBanner,
            },
        ],
        // Add widget under specific BetOffer Category Filter Tab in the event page
        eventBetOffersCategories: [
            {
                order: 1,
                widgetId: WIDGET_IDS.betBuilderVideo,
                args: {category: "BET-BUILDER"},
            },
        ],
        // Places widgets just above the odds section of a match page
        eventBeforeBetOffers: [
            {
                order: 1,
                widgetId: WIDGET_IDS.shortMatchInfo,
            },
        ],
        // Places widgets on the betslip, below the "Place bet" button
        betslipMain: [
            {
                order: 1,
                widgetId: WIDGET_IDS.betslipBalance,
            },
            {
                order: 2,
                widgetId: WIDGET_IDS.insufficientBalance,
            },
        ],
        // Places widgets at the far right column below the prematch stats
        eventColumn2Placeholder2: [
            {
                order: 1,
                widgetId: WIDGET_IDS.matchPreview,
            },
            {
                order: 2,
                widgetId: WIDGET_IDS.eventStatisticsButton,
            },
            {
                order: 3,
                widgetId: WIDGET_IDS.headToHead,
            },
        ],
    },
};
